import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Button, Center, Stack, Text, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { boardApi } from '../../api/endpoints.js';
import { NewBoardModal } from '../../features/boards/NewBoardModal.js';
import { usePermissions } from '../../features/auth/useSession.js';

/**
 * The root of the signed-in app is not a page — it forwards to whichever board
 * comes first, so a bookmark of "/" always lands somewhere useful.
 */
export const Route = createFileRoute('/_authed/')({
  loader: async ({ context }) => {
    const boards = await context.queryClient.fetchQuery({
      queryKey: ['boards'],
      queryFn: () => boardApi.list(),
    });

    const first = boards[0];
    if (first) {
      throw redirect({ to: '/board/$boardId', params: { boardId: first.slug } });
    }
    return { empty: true as const };
  },
  component: NoBoards,
});

/**
 * What a brand-new installation lands on.
 *
 * An admin gets the button that fixes it, right here. Sending them to Settings
 * instead — which is what this did — was a dead end: nothing on that page
 * creates a board, and categories and team are both scoped to a board that does
 * not exist yet.
 */
function NoBoards() {
  const { isAdmin } = usePermissions();
  const [creating, setCreating] = useState(false);

  return (
    <Center h="100%" p="xl">
      <Stack align="center" gap="xs" maw={420}>
        <Title order={3}>No boards yet</Title>
        <Text c="dimmed" ta="center" size="sm">
          {isAdmin
            ? 'Create the first board and the team can start tracking work on it.'
            : 'An admin needs to create the first board before there is anything to track.'}
        </Text>

        {isAdmin && (
          <>
            <Button
              mt="sm"
              leftSection={<IconPlus size={16} />}
              onClick={() => setCreating(true)}
            >
              Create a board
            </Button>
            <NewBoardModal opened={creating} onClose={() => setCreating(false)} />
          </>
        )}
      </Stack>
    </Center>
  );
}
